export interface ActiveTurnScope {
    userId: number | null;
    organizationId: number | null;
    conversationId: number | string;
}

export interface CapturedTurnIdentity extends ActiveTurnScope {
    authSessionEpoch: number;
}

export type ActiveTurnResolution =
    | { kind: "match"; assistantMessageId: number }
    | { kind: "stale" }
    | { kind: "none" };

interface ActiveTurnRecord {
    assistantMessageId: number;
    authSessionEpoch: number;
    conversationId: string;
}

const activeTurns = new Map<string, ActiveTurnRecord>();

function scopeKey(scope: ActiveTurnScope): string {
    return JSON.stringify([
        scope.userId ?? null,
        scope.organizationId ?? null,
        String(scope.conversationId),
    ]);
}

export function rememberActiveTurn(
    identity: CapturedTurnIdentity,
    assistantMessageId: number
): void {
    if (!Number.isInteger(assistantMessageId) || assistantMessageId <= 0) {
        forgetActiveTurn(identity);
        return;
    }
    activeTurns.set(scopeKey(identity), {
        assistantMessageId,
        authSessionEpoch: identity.authSessionEpoch,
        conversationId: String(identity.conversationId),
    });
}

export function forgetActiveTurn(scope: ActiveTurnScope): void {
    activeTurns.delete(scopeKey(scope));
}

export function forgetActiveTurnIf(
    scope: ActiveTurnScope,
    assistantMessageId: number
): void {
    if (
        activeTurns.get(scopeKey(scope))?.assistantMessageId ===
        assistantMessageId
    ) {
        forgetActiveTurn(scope);
    }
}

/** Epoch-blind registry probe for tests; production readers use resolveActiveTurn. */
export function peekActiveTurnForTesting(
    scope: ActiveTurnScope
): number | null {
    return activeTurns.get(scopeKey(scope))?.assistantMessageId ?? null;
}

/**
 * `stale` and `none` are not interchangeable: a turn held under another
 * principal, organization, or auth session must not fall back to the
 * conversation-wide abandon, because two organizations can hold the same
 * conversation id.
 */
export function resolveActiveTurn(
    identity: CapturedTurnIdentity
): ActiveTurnResolution {
    const record = activeTurns.get(scopeKey(identity));
    if (record) {
        return record.authSessionEpoch === identity.authSessionEpoch
            ? { kind: "match", assistantMessageId: record.assistantMessageId }
            : { kind: "stale" };
    }

    const conversationId = String(identity.conversationId);
    for (const held of activeTurns.values()) {
        if (held.conversationId === conversationId) return { kind: "stale" };
    }
    return { kind: "none" };
}
